import React, { useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Grid,
  makeStyles,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ReceiptIcon from "@material-ui/icons/Receipt";
import PaymentIcon from "@material-ui/icons/Payment";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import brandTheme from "brandTheme/Theme";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { parseDateMDY } from "utils/Utils";
import { apiHandler } from "assets/api";
import { endpoint } from "assets/api/endpoint";

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#F6F8FC",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 30,

    [theme.breakpoints.down("sm")]: {
      padding: 15,
      alignItems: "flex-start",
    },
  },

  card: {
    width: "100%",
    maxWidth: 650,
    padding: 40,
    borderRadius: 18,
    textAlign: "center",

    [theme.breakpoints.down("sm")]: {
      padding: 24,
      borderRadius: 12,
    },

    [theme.breakpoints.down("xs")]: {
      padding: 18,
    },
  },

  icon: {
    fontSize: 90,

    [theme.breakpoints.down("sm")]: {
      fontSize: 70,
    },

    [theme.breakpoints.down("xs")]: {
      fontSize: 60,
    },
  },

  heading: {
    marginTop: 10,
    fontWeight: 700,
    color: "#16295C",

    [theme.breakpoints.down("xs")]: {
      fontSize: 26,
    },
  },

  subHeading: {
    marginTop: 10,
    fontWeight: 600,
    fontSize: 20,

    [theme.breakpoints.down("xs")]: {
      fontSize: 17,
    },
  },

  description: {
    marginTop: 20,
    color: "#666",
    lineHeight: 1.7,

    [theme.breakpoints.down("xs")]: {
      fontSize: 14,
    },
  },

  detailsCard: {
    marginTop: 25,
    padding: 20,
    borderRadius: 12,

    [theme.breakpoints.down("xs")]: {
      padding: 15,
    },
  },

  info: {
    marginTop: 25,
    padding: 15,
    borderRadius: 10,
    border: "1px solid #C8E2FF",
    background: "#F5FAFF",
    display: "flex",
    alignItems: "flex-start",

    [theme.breakpoints.down("xs")]: {
      padding: 12,
    },
  },

  buttons: {
    marginTop: 30,
  },

  primary: {
    background: "#15254F",
    color: "#fff",
    padding: "10px 35px",
    textTransform: "none",

    "&:hover": {
      background: "#102040",
    },

    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
}));

const DetailRow = ({ icon, label, value }) => (
  <Grid
    container
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    style={{ margin: "12px 0" }}
  >
    <Grid
      item
      xs={12}
      sm={6}
      style={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {icon}

      <Typography
        style={{
          marginLeft: 10,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>
    </Grid>

    <Grid item xs={12} sm={6}>
      <Typography
        align="right"
        style={{
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Grid>
  </Grid>
);

export default function PaymentStatus({ status = "success" }) {
  const navigate = useNavigate();
  const { authToken } = useSelector((state) => state.login.authData);
  const dispatch = useDispatch();
  const location = useLocation()
  useEffect(() => {
    const initialize = async () => {
      if (!authToken) {
        try {
          await apiHandler({
            url: endpoint.USER_LOGOUT_API,
            authToken,
          });
        } catch (err) {
          console.log(err);
        }

        dispatch({ type: "SIGNOUT" });
        sessionStorage.removeItem("session");
        navigate("/login");
        return;
      }

      if (location.search) {
        window.history.replaceState({}, "", location.pathname);
      }
    };

    initialize();
  }, [authToken, dispatch, navigate, location.pathname, location.search]);

  const classes = useStyles();
  const success = status === "success";
  const paymentDetails = useSelector((state) => state.login.userCurrentPaymentDetails);
  return (
    <div className={classes.root}>
      <Paper elevation={3} className={classes.card}>

        {success ? (
          <CheckCircleIcon
            className={classes.icon}
            style={{ color: brandTheme.secondaryColor }}
          />
        ) : (
          <CancelIcon
            className={classes.icon}
            style={{ color: brandTheme.errorColor }}
          />
        )}

        <Typography variant="h4" className={classes.heading}>
          {success ? "Payment Successful!" : "Payment Failed!"}
        </Typography>

        <Typography
          className={classes.subHeading}
          style={{
            color: success ? brandTheme.secondaryColor : brandTheme.errorColor,
          }}
        >
          {success
            ? "Your payment has been completed successfully."
            : "We could not process your payment."}
        </Typography>

        <Typography className={classes.description}>
          {success
            ? "Thank you for your payment. Your transaction was completed successfully."
            : "Your payment was not successful. Please check the details and try again."}
        </Typography>

        <Paper
          className={classes.detailsCard}
          style={{
            background: success ? "#EEF9F2" : "#FFF3F3",
          }}
          elevation={0}
        >
          <Typography
            align="left"
            style={{ fontWeight: 700 }}
          >
            Payment Details
          </Typography>

          <DetailRow
            icon={<ReceiptIcon style={{color:success?brandTheme.secondaryColor:brandTheme.errorColor}} />}
            label="Transaction ID"
            value={paymentDetails?.transactionId}
          />

          <DetailRow
            icon={<PaymentIcon style={{color:success?brandTheme.secondaryColor:brandTheme.errorColor}} />}
            label={success ? "Amount Paid" : "Amount"}
            value={paymentDetails?.amount}
          />

          <DetailRow
            icon={<PaymentIcon style={{color:success?brandTheme.secondaryColor:brandTheme.errorColor}} />}
            label="Payment Mode"
            value="UPI"
          />

          <DetailRow
            icon={<AccessTimeIcon style={{color:success?brandTheme.secondaryColor:brandTheme.errorColor}} />}
            label="Date"
            value={parseDateMDY(paymentDetails?.date)}
          />

          {!success && (
            <DetailRow
              icon={<ErrorOutlineIcon style={{color:brandTheme.errorColor}} />}
              label="Reason"
              value="Payment was declined."
            />
          )}
        </Paper>

        <Paper className={classes.info} elevation={0}>
        <InfoOutlinedIcon
          color="primary"
          style={{ marginTop: 2, flexShrink: 0 }}
        />
      
        <Typography
          style={{
            marginLeft: 12,
            wordBreak: "break-word",
          }}
        >
            {success
              ? "A payment confirmation has been sent to your registered mobile number and email."
              : "No amount has been deducted from your account."}
          </Typography>
        </Paper>

        <Grid
          container
          spacing={2}
          justifyContent="center"
          className={classes.buttons}
        >

          <Grid item xs={12} sm="auto">
            <Button
              fullWidth
              variant="contained"
              className={classes.primary}
              onClick={() => navigate("/Repayment")}
            >
              Go to Dashboard
            </Button>
          </Grid>
        </Grid>

      </Paper>
    </div>
  );
}